// Cognito SignUp through the sign-up-only app client (pentest 2026-10-30
// AUTH-4). The client the app signs in with can no longer write `email` or
// `custom:accountType`: with that right, a stolen access token could change the
// account's email (its sign-in name) and take the account over. Amplify's
// signUp always uses that client, so sign-up calls Cognito directly here with
// a client that can write those attributes but can't sign anyone in.
// Confirming the emailed code and signing in still go through Amplify.

export interface SignUpRequest {
  username: string;
  password: string;
  attributes: Record<string, string>;
}

export interface SignUpResult {
  userSub?: string;
  userConfirmed?: boolean;
}

/** The sign-up client id; a single-client local setup falls back to the app client. */
export function signUpClientId(): string | undefined {
  return import.meta.env.VITE_USER_POOL_SIGNUP_CLIENT_ID || import.meta.env.VITE_USER_POOL_CLIENT_ID;
}

/**
 * Errors carry Cognito's exception name (UsernameExistsException,
 * InvalidPasswordException, ...), as Amplify's do, so utils/cognitoErrors
 * shows the same messages.
 */
export async function signUpWithSignUpClient({ username, password, attributes }: SignUpRequest): Promise<SignUpResult> {
  const userPoolId: string | undefined = import.meta.env.VITE_USER_POOL_ID;
  const clientId = signUpClientId();
  if (!userPoolId || !clientId) {
    throw new Error('Sign-up is not configured (VITE_USER_POOL_ID / VITE_USER_POOL_SIGNUP_CLIENT_ID).');
  }
  // A pool id is "<region>_<id>".
  const region = userPoolId.split('_')[0];

  const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.SignUp',
    },
    body: JSON.stringify({
      ClientId: clientId,
      Username: username,
      Password: password,
      UserAttributes: Object.entries(attributes).map(([Name, Value]) => ({ Name, Value })),
    }),
  });

  const body: Record<string, unknown> = await response.json().catch(() => ({}));
  if (!response.ok) {
    // e.g. "com.amazonaws.cognito.identity.idp.model#UsernameExistsException"
    const type = String(body.__type ?? body.code ?? 'UnknownError');
    const error = new Error(String(body.message ?? body.Message ?? 'Sign-up failed'));
    error.name = type.split('#').pop() || type;
    throw error;
  }
  return {
    userSub: typeof body.UserSub === 'string' ? body.UserSub : undefined,
    userConfirmed: typeof body.UserConfirmed === 'boolean' ? body.UserConfirmed : undefined,
  };
}
